// Eval row drawer. Opens when a row is clicked in EvalsTab. Mirrors the
// legacy web/scripts/training-center.js's tcOpenEvalDrawer with the same
// six sections:
//
//   1. Query header — raw_query, plus user / org / datasource / time /
//      status / execution_time_ms in a definition list.
//   2. Generated SQL — preformatted SQL block.
//   3. Quality Scores — per-dimension score badges + the evaluator's
//      reason. Falls back to a friendly "no scores" or "fetch failed"
//      block when /admin/quality/details is empty / 404.
//   4. User Feedback — feedback_rating + feedback_comment, or a quiet
//      "no feedback submitted" line.
//   5. Trace ID — copyable; clicking flashes "Copied!" for ~1.5s.
//   6. View Response link — only when row.thread_id is set (web-source
//      rows). Deep-links to /?thread=N so the user can land on the
//      conversation that produced this eval.

import { useEffect, useState } from "react";
import type { OrgScope, QualityDetails, QueryEvalRow } from "../../api/training";
import { trainingApi } from "../../api/training";
import { ErrorBlock, LoadingBlock, formatDateTime, formatNumber } from "../admin/_shared";

interface Props {
  bearer: string;
  scope: OrgScope;
  row: QueryEvalRow | null;
  onClose: () => void;
}

export function EvalDrawer({ bearer, scope, row, onClose }: Props) {
  const [details, setDetails] = useState<QualityDetails | null>(null);
  const [scoresError, setScoresError] = useState<Error | null>(null);
  const [scoresLoading, setScoresLoading] = useState(false);

  useEffect(() => {
    if (!row) {
      setDetails(null);
      setScoresError(null);
      return;
    }
    if (!row.trace_id) {
      // No trace_id → nothing to fetch. Render the rest of the drawer
      // immediately; the Quality Scores section shows "no scores".
      setDetails(null);
      setScoresError(null);
      setScoresLoading(false);
      return;
    }
    let cancelled = false;
    setScoresLoading(true);
    setScoresError(null);
    trainingApi
      .qualityDetails(bearer, { ...scope, trace_id: row.trace_id })
      .then((d) => {
        if (cancelled) return;
        setDetails(d);
        setScoresLoading(false);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setScoresError(e);
        setScoresLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bearer, scope, row]);

  // ESC closes — matches the SessionDrawer + ReportDrawer conventions.
  useEffect(() => {
    if (!row) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [row, onClose]);

  if (!row) return null;

  const title = row.raw_query || "(empty query)";
  const truncatedTitle = title.length > 80 ? `${title.slice(0, 80)}…` : title;
  const headerMeta = [
    row.user_email,
    row.organization,
    row.datasource_name,
    row.created_at ? formatDateTime(row.created_at) : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} role="presentation" />
      <aside className="fixed right-0 top-0 z-50 h-full w-full max-w-3xl bg-white shadow-2xl border-l border-neutral-200 overflow-y-auto">
        <header className="sticky top-0 z-10 bg-white border-b border-neutral-200 px-5 py-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-neutral-900 break-words">
              {truncatedTitle}
            </h2>
            {headerMeta && (
              <p className="text-xs text-neutral-500 mt-0.5 truncate">{headerMeta}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-500 hover:text-neutral-900 px-2 py-1 rounded"
            aria-label="Close"
          >
            ✕
          </button>
        </header>

        <div className="p-5 space-y-5">
          <QueryInfoSection row={row} />
          {row.sql_generated && <SqlSection sql={row.sql_generated} />}
          <QualityScoresSection
            details={details}
            loading={scoresLoading}
            error={scoresError}
            hasTraceId={Boolean(row.trace_id)}
          />
          <FeedbackSection rating={row.feedback_rating} comment={row.feedback_comment} />
          {row.trace_id && <TraceIdSection traceId={row.trace_id} />}
          {row.thread_id && (
            <a
              href={`/?thread=${row.thread_id}`}
              className="inline-flex items-center gap-1 rounded-md bg-brand text-white px-3 h-9 text-sm font-medium hover:bg-brand-600"
            >
              View full conversation →
            </a>
          )}
        </div>
      </aside>
    </>
  );
}

function QueryInfoSection({ row }: { row: QueryEvalRow }) {
  const statusClass =
    row.status === "completed"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : row.status === "error"
        ? "bg-red-50 text-red-700 border-red-200"
        : "bg-neutral-50 text-neutral-700 border-neutral-200";

  return (
    <section className="space-y-3">
      <SectionTitle>Query</SectionTitle>
      <pre className="rounded-md border border-line bg-neutral-50 p-3 text-sm text-neutral-800 whitespace-pre-wrap break-words">
        {row.raw_query}
      </pre>
      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-xs">
        <Field label="User">{row.user_email || "—"}</Field>
        <Field label="Organization">{row.organization || "—"}</Field>
        <Field label="Datasource">{row.datasource_name || "—"}</Field>
        <Field label="Time">{row.created_at ? formatDateTime(row.created_at) : "—"}</Field>
        <Field label="Status">
          {row.status ? (
            <span
              className={`inline-block px-1.5 py-0.5 border rounded text-[10px] uppercase tracking-wide ${statusClass}`}
            >
              {row.status}
            </span>
          ) : (
            "—"
          )}
        </Field>
        <Field label="Execution">
          {row.execution_time_ms != null
            ? `${formatNumber(Math.round(row.execution_time_ms))} ms`
            : "—"}
        </Field>
      </dl>
    </section>
  );
}

function SqlSection({ sql }: { sql: string }) {
  return (
    <section className="space-y-2">
      <SectionTitle>Generated SQL</SectionTitle>
      <pre className="rounded-md border border-line bg-neutral-50 p-3 font-mono text-[12px] text-neutral-800 whitespace-pre-wrap break-words max-h-96 overflow-y-auto">
        {sql}
      </pre>
    </section>
  );
}

function QualityScoresSection({
  details,
  loading,
  error,
  hasTraceId,
}: {
  details: QualityDetails | null;
  loading: boolean;
  error: Error | null;
  hasTraceId: boolean;
}) {
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <SectionTitle>Quality Scores</SectionTitle>
        {details?.overall_score != null && <QualityBadge value={details.overall_score} label="Overall" />}
      </div>
      {!hasTraceId ? (
        <NoData>No trace recorded for this query — quality scores unavailable.</NoData>
      ) : loading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorBlock error={error} />
      ) : !details || details.scores.length === 0 ? (
        <NoData>No quality scores available.</NoData>
      ) : (
        <div className="space-y-2">
          {details.scores.map((s) => (
            <div
              key={s.score_name}
              className="rounded-md border border-line bg-white p-3 text-xs space-y-1"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="font-medium text-neutral-900">{dimensionLabel(s.score_name)}</div>
                <QualityBadge value={s.score_value} />
              </div>
              {s.comment && (
                <div className="text-neutral-700 whitespace-pre-wrap break-words">{s.comment}</div>
              )}
              {s.evaluator_model && (
                <div className="text-[10px] text-neutral-500">
                  evaluator: {s.evaluator_model}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function FeedbackSection({
  rating,
  comment,
}: {
  rating?: string | null;
  comment?: string | null;
}) {
  return (
    <section className="space-y-2">
      <SectionTitle>User Feedback</SectionTitle>
      {!rating ? (
        <NoData>No feedback submitted.</NoData>
      ) : (
        <div className="rounded-md border border-line bg-white p-3 space-y-1.5">
          <RatingBadge value={rating} />
          {comment && (
            <div className="text-xs text-neutral-700 whitespace-pre-wrap break-words">
              {comment}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function TraceIdSection({ traceId }: { traceId: string }) {
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(traceId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Some sandboxes block clipboard writes silently — fall back to a
      // soft no-op rather than a noisy error.
    }
  };

  return (
    <section className="space-y-2">
      <SectionTitle>Trace</SectionTitle>
      <button
        type="button"
        onClick={onCopy}
        className="text-xs text-neutral-700 font-mono inline-flex items-center gap-2 rounded-md border border-line bg-neutral-50 px-2.5 py-1.5 hover:bg-chip"
        title="Click to copy"
      >
        <span className="break-all">{traceId}</span>
        <span className="text-[10px] text-neutral-500 shrink-0">
          {copied ? "✓ copied" : "copy"}
        </span>
      </button>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------
function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
      {children}
    </h3>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-neutral-500">{label}</dt>
      <dd className="text-neutral-800">{children}</dd>
    </>
  );
}

function NoData({ children }: { children: React.ReactNode }) {
  return <div className="text-xs text-neutral-500 italic">{children}</div>;
}

function QualityBadge({ value, label }: { value: number | null; label?: string }) {
  if (value == null)
    return <span className="text-[11px] text-neutral-500">{label ? `${label}: —` : "—"}</span>;
  const pct = Math.round(value * 100);
  const cls =
    pct >= 80
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : pct >= 50
        ? "bg-amber-50 text-amber-700 border-amber-200"
        : "bg-red-50 text-red-700 border-red-200";
  return (
    <span
      className={`inline-block rounded border text-[11px] font-medium px-1.5 py-0.5 ${cls} tabular-nums`}
    >
      {label ? `${label}: ` : ""}
      {pct}%
    </span>
  );
}

function RatingBadge({ value }: { value: string }) {
  const isGood = value === "Good";
  const cls = isGood
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : "bg-red-50 text-red-700 border-red-200";
  return (
    <span
      className={`inline-block rounded-full border text-[11px] font-medium px-2 py-0.5 ${cls}`}
    >
      {isGood ? "👍 " : "👎 "}
      {value}
    </span>
  );
}

const DIM_LABEL_OVERRIDES: Record<string, string> = {
  sql_correctness: "SQL Correctness",
  relevance: "Relevance",
  entity_resolution: "Entity Resolution",
  output_columns: "Output Columns",
  table_plan: "Table Plan",
  semantic_sql: "Semantic SQL",
  answer_quality: "Answer Quality",
};

function dimensionLabel(name: string): string {
  return DIM_LABEL_OVERRIDES[name] ?? prettify(name);
}

function prettify(name: string): string {
  return name
    .replace(/_/g, " ")
    .replace(/\b(\w)/g, (m) => m.toUpperCase());
}
